import { isNil, isUndefined } from "lodash";
import { Resolver } from "react-hook-form";

import { BankType, Maybe, ParticipantTypes } from "@api/graphql";
import { bankDetailsErrorResolver } from "@components/account_profile/account_details/bankDetailsErrorResolver";
import { isAmpCmoArtCentre, isArtistBeneficiary } from "@util/index";
import { EMAIL, INTERNATIONAL_PHONE } from "@util/regex";

import { UserDetailsInput, ValidationError } from "../steps/RegisterFormTypes";

export const ERROR_MESSAGES = {
  mandatory: "This is a mandatory field.",
  invalidPhone: "Invalid mobile number. Must be 10 digits.",
  invalidEmail: "Invalid email address.",
  invalidABN: "Invalid ABN. Must pass the format validation.",
  invalidCompanyName: "Company/Trading name contains invalid characters.",
  abnRegistered: "You must select this option if the ABN is already registered.",
  invalidBSB: "BSB must be exactly 6 digits.",
  invalidResident: "Recipients must be Australian residents for tax purposes when creating a new participant.",
};
interface MandatoryFieldParams {
  isArtist?: boolean;
  isBeneficiary?: boolean;
  isCollaboration?: boolean;
  isInternalUserCreatingAccount?: boolean;
  isJoiningExistingAccount?: boolean;
  isReporter?: boolean;
  isAmp?: boolean;
  isCmo?: boolean;
  isArtCenter?: boolean;
  isAccountActive?: boolean;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

const isAddressFieldsMandatory = ({
  isInternalUserCreatingAccount,
  isAccountActive,
  accountStatus,
  participantTypes,
}: MandatoryFieldParams): boolean => {
  // Apply dynamic mandatory fields when both accountStatus and participantTypes are provided
  if (accountStatus && participantTypes) {
    if (accountStatus === "INCOMPLETE" || accountStatus === "DECEASED" || accountStatus === "INDIRECT") {
      return false;
    }
    if (isAmpCmoArtCentre(participantTypes)) {
      if (accountStatus === "ACTIVE" || accountStatus === "PENDING_APPROVAL") {
        return true;
      }
    }
    if (isArtistBeneficiary(participantTypes)) {
      if (accountStatus === "ACTIVE" || accountStatus === "PENDING_APPROVAL") {
        return true;
      }
    }
  }
  // Fallback to original logic for backward compatibility
  return (
    (isInternalUserCreatingAccount !== true && isNil(isAccountActive)) ||
    (!isInternalUserCreatingAccount && isAccountActive === true)
  );
};

export const mandatoryFields = {
  taxDetails: {
    companyTradingName: ({
      isArtist,
      isBeneficiary,
      isJoiningExistingAccount,
      accountStatus,
      participantTypes,
    }: MandatoryFieldParams) => {
      // Apply dynamic mandatory fields when account editing (accountStatus and participantTypes are provided)
      if (accountStatus && participantTypes) {
        if (isAmpCmoArtCentre(participantTypes)) {
          if (accountStatus === "ACTIVE" || accountStatus === "INCOMPLETE" || accountStatus === "PENDING_APPROVAL") {
            return true;
          }
        }

        // Company/Trading name is always optional for Artist/Beneficiary
        if (isArtistBeneficiary(participantTypes)) {
          return false;
        }
      }
      // Fallback to original logic for backward compatibility
      return !(isArtist || isBeneficiary) && !isJoiningExistingAccount;
    },
    abnAcn: ({ isAmp, isArtCenter, accountStatus, participantTypes }: MandatoryFieldParams) => {
      // ABN is optional for account editing (when accountStatus and participantTypes are provided)
      if (accountStatus && participantTypes) {
        return false;
      }
      // Keep original logic for registration forms
      return isAmp || isArtCenter;
    },
    legalEntityName: ({ isReporter, accountStatus, participantTypes }: MandatoryFieldParams) => {
      // Legal Entity Name is optional for account editing (when accountStatus and participantTypes are provided)
      if (accountStatus && participantTypes) {
        return false;
      }
      // Keep original logic for registration forms
      return !!isReporter;
    },
    residencyCitizenship: ({ accountStatus, participantTypes }: MandatoryFieldParams) => {
      // Residency/Citizenship mandatory for Artist/Beneficiary when ACTIVE or PENDING_APPROVAL
      if (accountStatus && participantTypes) {
        if (accountStatus === "INCOMPLETE" || accountStatus === "DECEASED" || accountStatus === "INDIRECT") {
          return false;
        }
        if (isArtistBeneficiary(participantTypes)) {
          return accountStatus === "ACTIVE" || accountStatus === "PENDING_APPROVAL";
        }
      }
      return false;
    },
  },
  contactDetails: {
    phoneNumber: ({ isArtist, isBeneficiary, isCollaboration, isInternalUserCreatingAccount }: MandatoryFieldParams) =>
      (isArtist || isBeneficiary || isCollaboration) && !isInternalUserCreatingAccount,
  },
  financeContact: {
    firstName: ({ isInternalUserCreatingAccount }: MandatoryFieldParams) => !isInternalUserCreatingAccount,
    lastName: ({ isInternalUserCreatingAccount }: MandatoryFieldParams) => !isInternalUserCreatingAccount,
    email: ({ isInternalUserCreatingAccount }: MandatoryFieldParams) => !isInternalUserCreatingAccount,
    phoneNumber: ({ isInternalUserCreatingAccount }: MandatoryFieldParams) => !isInternalUserCreatingAccount,
  },
  accountDetails: {
    yearOfBirth: ({
      isInternalUserCreatingAccount,
      isArtist,
      isBeneficiary,
      isCollaboration,
      isAmp,
      isCmo,
    }: MandatoryFieldParams) =>
      (isArtist || isBeneficiary || isCollaboration) && !isAmp && !isCmo && !isInternalUserCreatingAccount,
    isIndigenous: ({
      isInternalUserCreatingAccount,
      isArtist,
      isBeneficiary,
      isCollaboration,
      isAmp,
      isCmo,
    }: MandatoryFieldParams) =>
      (isArtist || isBeneficiary || isCollaboration) && !isAmp && !isCmo && !isInternalUserCreatingAccount,
    address: {
      address_line_1: isAddressFieldsMandatory,
      city: isAddressFieldsMandatory,
      state: isAddressFieldsMandatory,
      postcode: isAddressFieldsMandatory,
      country: isAddressFieldsMandatory,
    },
  },
};

export type ValidationErrors<T> = {
  [K in keyof T]?: T[K] extends object ? ValidationErrors<T[K]> : ValidationError;
};

// ABN Validation Function from https://abr.business.gov.au/Help/AbnFormat
export const validateABN = (abn: string): boolean => {
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  abn = abn.replace(/[^\d]/g, "");
  if (abn.length !== 11) {
    return false;
  }

  const sum = abn
    .split("")
    .reduce((acc, digit, idx) => acc + weights[idx] * (parseInt(digit, 10) - (idx === 0 ? 1 : 0)), 0);

  return sum % 89 === 0;
};

const isValidValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0 && !value.every(isUndefined);
  }
  return true;
};

const validateRequiredFields = <
  T extends Record<string, string | boolean | number | Maybe<string>[] | boolean[] | number[]>,
>(
  fields: readonly (keyof T)[],
  values: T,
  errorContainer: ValidationErrors<T> = {},
): ValidationErrors<T> | undefined => {
  fields.forEach((field) => {
    if (!isValidValue(values[field])) {
      if (!errorContainer) {
        errorContainer = {} as ValidationErrors<T>;
      }

      errorContainer[field] = {
        type: "required",
        message: ERROR_MESSAGES.mandatory,
      } as ValidationErrors<T>[typeof field];
    }
  });

  return Object.keys(errorContainer).length ? errorContainer : undefined;
};
const validateEmail = (email: string | undefined): ValidationError | undefined => {
  if (email && !EMAIL.test(email)) {
    return { type: "pattern", message: ERROR_MESSAGES.invalidEmail };
  }
  return undefined;
};
const validatePhoneNumber = (phone: string | undefined): ValidationError | undefined => {
  if (phone && !INTERNATIONAL_PHONE.test(phone)) {
    return { type: "pattern", message: ERROR_MESSAGES.invalidPhone };
  }
  return undefined;
};

const validateSection = {
  contactDetails: (
    values: UserDetailsInput,
    context: {
      isInternalUserCreatingAccount: boolean;
      isArtist: boolean;
      isBeneficiary: boolean;
      isCollaboration: boolean;
    },
    errors: ValidationErrors<UserDetailsInput>,
  ) => {
    const { isInternalUserCreatingAccount, isArtist, isBeneficiary, isCollaboration } = context;
    const contactDetailsVisible = isInternalUserCreatingAccount
      ? isInternalUserCreatingAccount && (isArtist || isBeneficiary) && !isCollaboration
      : true;

    if (contactDetailsVisible) {
      const requiredFields: (keyof UserDetailsInput["contact_details"])[] = ["first_name", "last_name"];

      // Add phone number validation for collaboration accounts
      if (
        mandatoryFields.contactDetails.phoneNumber({
          isArtist,
          isBeneficiary,
          isCollaboration,
          isInternalUserCreatingAccount,
        })
      ) {
        requiredFields.push("phone_number");
      }

      const contactErrors = validateRequiredFields(requiredFields, values.contact_details || {});
      if (contactErrors) {
        errors.contact_details = contactErrors;
      }
    }
  },

  accountDetails: (
    values: UserDetailsInput,
    context: {
      isArtist: boolean;
      isBeneficiary: boolean;
      isCollaboration: boolean;
      isInternalUserCreatingAccount: boolean;
      isCreatedUserCompletingRegistration: boolean;
      isCMO: boolean;
      isArtCenter: boolean;
      isAmp: boolean;
      isJoiningExistingAccount: boolean;
      isUserActive: boolean;
    },
    errors: ValidationErrors<UserDetailsInput>,
  ) => {
    const {
      isArtist,
      isBeneficiary,
      isCollaboration,
      isInternalUserCreatingAccount,
      isCreatedUserCompletingRegistration,
      isCMO,
      isAmp,
      isJoiningExistingAccount,
    } = context;

    if (isCreatedUserCompletingRegistration) {
      return;
    }

    if (isJoiningExistingAccount) {
      return;
    }

    if (
      mandatoryFields.accountDetails.yearOfBirth({
        isInternalUserCreatingAccount,
        isArtist,
        isBeneficiary,
        isCollaboration,
        isAmp,
        isCmo: isCMO,
      }) &&
      !values.account_details?.year_of_birth
    ) {
      errors.account_details = {
        ...errors.account_details,
        year_of_birth: { type: "required", message: ERROR_MESSAGES.mandatory },
      };
    }

    if (
      mandatoryFields.accountDetails.isIndigenous({
        isInternalUserCreatingAccount,
        isArtist,
        isBeneficiary,
        isCollaboration,
        isAmp,
        isCmo: isCMO,
      }) &&
      !values.account_details?.is_indigenous
    ) {
      errors.account_details = {
        ...errors.account_details,
        is_indigenous: { type: "required", message: ERROR_MESSAGES.mandatory },
      };
    }

    if (
      mandatoryFields.accountDetails.address.address_line_1({
        isInternalUserCreatingAccount,
      }) &&
      !values.account_details?.address?.address_line_1
    ) {
      errors.account_details = {
        ...errors.account_details,
        address: {
          ...errors.account_details?.address,
          address_line_1: {
            type: "required",
            message: ERROR_MESSAGES.mandatory,
          } as ValidationError,
        },
      } as ValidationErrors<UserDetailsInput>["account_details"];
    }

    // Suburb/City validation
    if (
      mandatoryFields.accountDetails.address.city({
        isInternalUserCreatingAccount,
      }) &&
      !values.account_details?.address?.city
    ) {
      errors.account_details = {
        ...errors.account_details,
        address: {
          ...errors.account_details?.address,
          city: {
            type: "required",
            message: ERROR_MESSAGES.mandatory,
          } as ValidationError,
        },
      } as ValidationErrors<UserDetailsInput>["account_details"];
    }

    // Postcode validation - allow any non-empty string
    const postcode = values.account_details?.address?.postcode;
    const isPostcodeNonEmptyString = String(postcode ?? "").trim() !== "";
    if (
      mandatoryFields.accountDetails.address.postcode({
        isInternalUserCreatingAccount,
      }) &&
      !isPostcodeNonEmptyString
    ) {
      errors.account_details = {
        ...errors.account_details,
        address: {
          ...errors.account_details?.address,
          postcode: {
            type: "required",
            message: ERROR_MESSAGES.mandatory,
          } as ValidationError,
        },
      } as ValidationErrors<UserDetailsInput>["account_details"];
    }

    // State validation
    if (
      mandatoryFields.accountDetails.address.state({
        isInternalUserCreatingAccount,
      }) &&
      !values.account_details?.address?.state
    ) {
      errors.account_details = {
        ...errors.account_details,
        address: {
          ...errors.account_details?.address,
          state: {
            type: "required",
            message: ERROR_MESSAGES.mandatory,
          } as ValidationError,
        },
      } as ValidationErrors<UserDetailsInput>["account_details"];
    }

    if (!values.account_details?.address?.country) {
      errors.account_details = {
        ...errors.account_details,
        address: {
          ...errors.account_details?.address,
          country: {
            type: "required",
            message: ERROR_MESSAGES.mandatory,
          } as ValidationError,
        },
      } as ValidationErrors<UserDetailsInput>["account_details"];
    }
  },

  financeContact: (
    values: UserDetailsInput,
    context: {
      isInternalUserCreatingAccount: boolean;
      isRoyaltyRecipientOnly: boolean;
      isJoiningExistingAccount: boolean;
      isCreatedUserCompletingRegistration: boolean;
    },
    errors: ValidationErrors<UserDetailsInput>,
  ) => {
    const {
      isInternalUserCreatingAccount,
      isRoyaltyRecipientOnly,
      isJoiningExistingAccount,
      isCreatedUserCompletingRegistration,
    } = context;

    if (isCreatedUserCompletingRegistration && isRoyaltyRecipientOnly) {
      return;
    }

    const isFieldMandatory = (field: keyof typeof mandatoryFields.financeContact) =>
      mandatoryFields.financeContact[field]({ isInternalUserCreatingAccount });

    if (!isInternalUserCreatingAccount && !isRoyaltyRecipientOnly && !isJoiningExistingAccount) {
      const requiredFields = [] as (keyof UserDetailsInput["finance_contact"])[];

      if (isFieldMandatory("firstName")) {
        requiredFields.push("first_name");
      }
      if (isFieldMandatory("lastName")) {
        requiredFields.push("last_name");
      }
      if (isFieldMandatory("email")) {
        requiredFields.push("finance_email");
      }
      if (isFieldMandatory("phoneNumber")) {
        requiredFields.push("finance_phone");
      }

      const financeErrors = validateRequiredFields(requiredFields, values.finance_contact || {});
      if (financeErrors) {
        errors.finance_contact = financeErrors;
      }
    }

    const emailValue = values.finance_contact?.finance_email;
    if (isFieldMandatory("email") || emailValue) {
      const emailError = validateEmail(emailValue as string | undefined);
      if (emailError) {
        errors.finance_contact = { ...errors.finance_contact, finance_email: emailError };
      }
    }

    const phoneValue = values.finance_contact?.finance_phone;
    if (isFieldMandatory("phoneNumber") || phoneValue) {
      const phoneError = validatePhoneNumber(phoneValue as string | undefined);
      if (phoneError) {
        errors.finance_contact = { ...errors.finance_contact, finance_phone: phoneError };
      }
    }
  },
  taxDetails: (
    values: UserDetailsInput,
    context: {
      isArtist: boolean;
      isBeneficiary: boolean;
      isInternalUserCreatingAccount: boolean;
      isRoyaltyRecipientOnly: boolean;
      isJoiningExistingAccount: boolean;
      isAbnRegistered: boolean;
      isAmp: boolean;
      isCMO: boolean;
      isArtCenter: boolean;
    },
    errors: ValidationErrors<UserDetailsInput>,
  ) => {
    const {
      isArtist,
      isBeneficiary,
      isInternalUserCreatingAccount,
      isRoyaltyRecipientOnly,
      isJoiningExistingAccount,
      isAbnRegistered,
    } = context;

    if (
      mandatoryFields.taxDetails.companyTradingName({ isArtist, isBeneficiary, isJoiningExistingAccount }) &&
      !values.tax_details?.company_trading_name
    ) {
      errors.tax_details = {
        ...errors.tax_details,
        company_trading_name: { type: "required", message: ERROR_MESSAGES.mandatory },
      };
    }

    if (
      isRoyaltyRecipientOnly &&
      !isJoiningExistingAccount &&
      values.tax_details?.is_aus_resident === undefined &&
      !isInternalUserCreatingAccount
    ) {
      // In the case that is_aus_resident is a required field, we need to manually send it as false if the field is not checked to backend.
      // Setting an error will force user to check this field, but they may or may not want to check this field.
      // We need to only unsure that for these cases, the value of is_aus_resident is not undefined, it could ether be true when checked, or we can set it manually to false only in this scenario when not checked.
      values.tax_details.is_aus_resident = false;
    }

    // Validate `join_existing_participant` if ABN is registered
    if (isAbnRegistered && !isArtist && !isBeneficiary && !values.join_existing_participant) {
      errors.join_existing_participant = {
        type: "required",
        message: ERROR_MESSAGES.abnRegistered,
      };
    }

    // check if should show citizenship fields
    const showCitizenshipFields = !isJoiningExistingAccount && isRoyaltyRecipientOnly;
    if (showCitizenshipFields) {
      if (values.tax_details?.residency_citizenship?.length === 0) {
        errors.tax_details = {
          ...errors.tax_details,
          residency_citizenship: { type: "required", message: ERROR_MESSAGES.mandatory },
        };
      }
    }
  },

  bankDetails: (
    values: UserDetailsInput,
    context: {
      isCreatedUserCompletingRegistration: boolean;
    },
    errors: ValidationErrors<UserDetailsInput>,
  ) => {
    const { isCreatedUserCompletingRegistration } = context;

    if (isCreatedUserCompletingRegistration) {
      return;
    }

    if (values.bank_details.bank_type === BankType.DOMESTIC) {
      const { errors: domesticBankErrors } = bankDetailsErrorResolver(values, BankType.DOMESTIC);
      if (domesticBankErrors.bank_details) {
        errors.bank_details = {
          ...errors.bank_details,
          ...domesticBankErrors.bank_details,
        };
      }
    }

    if (values.bank_details.bank_type === BankType.OVERSEAS) {
      const { errors: overseasBankErrors } = bankDetailsErrorResolver(values, BankType.OVERSEAS);
      if (overseasBankErrors.bank_details) {
        errors.bank_details = {
          ...errors.bank_details,
          ...overseasBankErrors.bank_details,
        };
      }
    }
  },
};

// Main Resolver
export const errorResolver: Resolver<UserDetailsInput> = async (values, context) => {
  const errors: ValidationErrors<UserDetailsInput> = {};
  const contextWithTypes = {
    ...context,
    isCollaboration: context.isCollaboration || false,
  };
  validateSection.contactDetails(values, contextWithTypes, errors);
  validateSection.accountDetails(values, contextWithTypes, errors);
  validateSection.financeContact(values, context, errors);
  validateSection.taxDetails(values, context, errors);
  validateSection.bankDetails(values, context, errors);

  return {
    values: Object.keys(errors).length === 0 ? values : {},
    errors,
  };
};
